import { GridColDef } from "@mui/x-data-grid-pro";
import {
  CircularProgress,
  FormControlLabel,
  GKDataGrid,
  Grid2,
  Switch,
  Typography,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField } from "utils/helpers";
import useDocumentTitle from "utils/useDocumentTitle";

const PartnerAccess = () => {
  useDocumentTitle("Portfolio Access");

  const { id } = useParams();

  const [portfolioList, setPortfolioList] = useState<any>([]);
  const [switchLoading, setSwitchLoading] = useState("");
  const [selectedPortfolioList, setSelectedPortfolioList] = useState([]);

  const requestBody = useGenerateQueryParams("partnerPortfolioAccess");

  const { isFetching, refetch, isRefetching } = useFetchQuery({
    key: ["PARTNER_PORTFOLIO_ACCESS", requestBody],
    route: `/partner/access/${id}/portfolios/`,
    requestBody,
    success: (response: any) => {
      setPortfolioList(response?.data);
      setSelectedPortfolioList(response?.data?.portfolios);
    },
  });

  const { mutate: enableDisableCLient, isLoading: enableDisableCLientLoading } =
    useMutation({
      mutationFn: (arr: any) =>
        axiosInstance.patch(`/partner/access/${id}/portfolios/`, {
          portfolios: arr,
        }),
      onSuccess: () => {
        refetch();
      },
    });

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Portfolio", "name", 250, 1),
      renderCell: (params) => {
        return (
          <Grid2
            container
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Typography fontWeight={700}>{params?.row?.clientName}</Typography>
            <Typography>
              {params?.row?.name} - {params?.row?.portfolioId}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField("Family Name", "familyName", 150, 1),
      renderCell: (param) => (
        <Typography>{param?.row?.familyName || "-"}</Typography>
      ),
    },
    {
      ...extraColumnsField("Phone", "phone", 150, 1),
    },
    {
      ...extraColumnsField("PAN", "clientPan", 150, 1),
    },
    {
      ...extraColumnsField("Actions", "action", 100, 1),
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        return (enableDisableCLientLoading || isRefetching || isFetching) &&
          params?.row?.id === switchLoading ? (
          <CircularProgress size={18} />
        ) : (
          <Switch
            checked={selectedPortfolioList?.includes(params?.row?.id)}
            disabled={enableDisableCLientLoading || isRefetching || isFetching}
            onChange={(event) => {
              setSwitchLoading(params?.row?.id);
              let arr = [];
              if (event.target.checked) {
                arr = [...selectedPortfolioList, params?.row?.id];
              } else {
                arr = selectedPortfolioList?.filter(
                  (id: any) => id !== params?.row?.id,
                );
              }
              enableDisableCLient(arr);
            }}
          />
        );
      },
    },
  ];

  return (
    <GKDataGrid
      columns={columns}
      rows={portfolioList?.data || []}
      loading={selectedPortfolioList?.length === 0 && isFetching}
      headerComponent={
        <FormControlLabel
          control={
            <Switch
              checked={portfolioList?.enableAll}
              onChange={() => {
                axiosInstance
                  .patch(`/partner/access/${id}/all_portfolios/`)
                  .then(() => refetch())
                  .catch((error) => {
                    toast.error(
                      error?.response?.data?.message ||
                        "Something went wrong, please try again.",
                    );
                  });
              }}
            />
          }
          label={portfolioList?.enableAll ? "Disable All" : "Enable All"}
          labelPlacement="end"
        />
      }
      totalCount={portfolioList.totalCount || 0}
      name="partnerPortfolioAccess"
      exportFile={{
        path: `/partner/access/${id}/portfolios/`,
        exportName: "portfolio-access",
      }}
    />
  );
};

export default PartnerAccess;
